<template>
  <div class="vt-wrapper">
    <div
      class="virtual-table-container"
      :class="{
        'virtual-table-container--scrollable': !windowScroll,
        'virtual-table-container--window': windowScroll,
      }"
      ref="container"
      @scroll.passive="onScroll"
    >
      <slot v-if="items.length === 0" name="empty" />
      <table v-else :class="tableClass">
        <thead>
          <slot name="header" />
        </thead>
        <tbody>
          <slot name="before-rows" />
          <tr v-if="topPadding > 0" :style="{ height: topPadding + 'px' }">
            <td :colspan="colspan" class="vt-spacer"></td>
          </tr>
          <template v-for="(item, visibleIndex) in visibleItems">
            <slot name="row" :item="item" :index="startIndex + visibleIndex" />
          </template>
          <tr
            v-if="bottomPadding > 0"
            :style="{ height: bottomPadding + 'px' }"
          >
            <td :colspan="colspan" class="vt-spacer"></td>
          </tr>
          <slot name="after-rows" />
        </tbody>
      </table>
    </div>
    <!-- Scroll-shadow affordances: a gradient on an edge signals there are more
         columns to scroll to that way; it fades out once you reach the end. -->
    <div
      v-show="canScrollLeft"
      class="vt-scroll-shadow vt-scroll-shadow--left"
      aria-hidden="true"
    ></div>
    <div
      v-show="canScrollRight"
      class="vt-scroll-shadow vt-scroll-shadow--right"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script>
export default {
  name: "VirtualTable",
  props: {
    items: {
      type: Array,
      required: true,
    },
    /**
     * Estimated row height in px. Must be > 0. Used as a floor — actual
     * rendered heights are measured and may override this upward.
     **/
    rowHeight: {
      type: Number,
      default: 57,
      validator: (v) => v > 0,
    },
    /**
     * Extra rows to render above and below the visible window. Higher values
     * reduce blank-row flicker during fast scrolling at the cost of more DOM nodes.
     **/
    bufferSize: {
      type: Number,
      default: 10,
      validator: (v) => v >= 0,
    },
    /**
     * Number of columns in the table. Applied to the spacer <td> elements so
     * they span the full table width and preserve correct column alignment.
     **/
    colspan: {
      type: Number,
      required: true,
      validator: (v) => v >= 1,
    },
    tableClass: {
      type: String,
      default: "table table-sm table-striped reportTable mb-0",
    },
    /**
     * Height in px of non-virtual content rendered in the #before-rows slot
     * (e.g. a pinned totals row above the virtual list). Subtracted from the
     * effective scroll position so startIndex skips those rows correctly.
     **/
    topOffset: {
      type: Number,
      default: 0,
    },
    /**
     * When true, the browser window is the scroll container instead of the
     * internal fixed-height div. The table grows to its full natural height
     * and the page scrolls normally. Defaults to false.
     **/
    windowScroll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollTop: 0,
      containerHeight: 500,
      rafPending: false,
      effectiveRowHeight: this.rowHeight,
      canScrollLeft: false,
      canScrollRight: false,
    };
  },
  watch: {
    items() {
      /**
       * In container mode, reset scroll so stale scrollTop doesn't cause
       * oscillation when the dataset changes (e.g. group-by switch or new fetch).
       * In window-scroll mode, leave the page position alone — the user stays
       * where they are and virtual rows re-render at the current scroll offset.
       */
      if (!this.windowScroll) {
        this.scrollToTop();
      }
      this.$nextTick(() => this.measureRowHeight());
      // A new dataset can change the width (a scrollbar appearing/leaving), so
      // re-measure once the layout settles to avoid an initial flicker.
      this.remeasureSoon();
    },
    rowHeight(val) {
      this.effectiveRowHeight = val;
      this.$nextTick(() => this.measureRowHeight());
    },
  },
  computed: {
    visibleCount() {
      const rowH = this.effectiveRowHeight > 0 ? this.effectiveRowHeight : 1;
      return Math.ceil(this.containerHeight / rowH) + this.bufferSize * 2;
    },
    startIndex() {
      // If all items fit within visibleCount, disable virtual scrolling to
      // avoid content-height fluctuation from variable-height rows
      if (this.items.length <= this.visibleCount) return 0;

      /**
       * Pin the bottom window: never start past (length - visibleCount), so
       * at the end of the list the rendered set is always the same final
       * rows. Without the clamp, the bottom window is "whatever's left" —
       * its real pixel height varies with the tall/short row mix, the
       * document height shifts a few px per re-render, the browser clamps
       * the pinned-at-bottom scrollY, and that scroll event re-renders a
       * different window — an infinite flicker loop at the very bottom.
       */
      const maxStart = Math.max(0, this.items.length - this.visibleCount);

      if (this.windowScroll) {
        /**
         * Read scrollTop BEFORE the ref guard. Computed caching only tracks
         * dependencies that were actually read — if an evaluation bails on a
         * momentarily-detached ref (mid-patch after an items change) without
         * touching scrollTop, the 0 gets cached forever and the table
         * freezes on rows 0..N regardless of scrolling.
         */
        const scrollY = this.scrollTop;
        if (!this.$refs.container) return 0;
        const rect = this.$refs.container.getBoundingClientRect();
        // containerDocTop = absolute Y of container from document top
        const containerDocTop = rect.top + scrollY;
        const effectiveScroll = Math.max(
          0,
          scrollY - containerDocTop - this.topOffset
        );
        return Math.min(
          maxStart,
          Math.max(
            0,
            Math.floor(effectiveScroll / this.effectiveRowHeight) -
              this.bufferSize
          )
        );
      }

      const effectiveScroll = Math.max(0, this.scrollTop - this.topOffset);
      return Math.min(
        maxStart,
        Math.max(
          0,
          Math.floor(effectiveScroll / this.effectiveRowHeight) -
            this.bufferSize
        )
      );
    },
    endIndex() {
      return Math.min(this.items.length, this.startIndex + this.visibleCount);
    },
    visibleItems() {
      return this.items.slice(this.startIndex, this.endIndex);
    },
    topPadding() {
      return this.startIndex * this.effectiveRowHeight;
    },
    bottomPadding() {
      return Math.max(
        0,
        (this.items.length - this.endIndex) * this.effectiveRowHeight
      );
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.measureContainer();
      this.measureRowHeight();
      // Catch the final layout once width/scrollbars/fonts settle.
      this.remeasureSoon();

      if (this.windowScroll) {
        // Window scroll mode: listen on window for scroll and resize
        this._windowScrollHandler = () => {
          if (!this.rafPending) {
            this.rafPending = true;
            this._rafId = requestAnimationFrame(() => {
              this.scrollTop = window.scrollY;
              this.rafPending = false;
              this._rafId = null;
            });
          }
        };
        this._windowResizeHandler = () =>
          requestAnimationFrame(() => this.measureContainer());
        window.addEventListener("scroll", this._windowScrollHandler, {
          passive: true,
        });
        window.addEventListener("resize", this._windowResizeHandler, {
          passive: true,
        });
      }

      // ResizeObserver watches container width changes (text wrapping, layout shifts)
      // Works in both modes — triggers row height re-measurement on container resize
      if (typeof ResizeObserver !== "undefined") {
        this._lastObservedWidth = null;
        this._resizeObserver = new ResizeObserver((entries) => {
          /**
           * React to WIDTH changes only. The container's height changes on
           * every virtual-window swap (spacer padding moves between top and
           * bottom) — re-measuring on those creates a feedback loop:
           * measure → row-height estimate shifts → padding shifts → observer
           * fires → measure... which renders as high-speed row flicker.
           * Width is the only dimension that affects text wrapping, and
           * that's what this observer exists to catch.
           */
          const width =
            entries && entries[0] && entries[0].contentRect
              ? entries[0].contentRect.width
              : null;
          if (
            width !== null &&
            this._lastObservedWidth !== null &&
            Math.abs(width - this._lastObservedWidth) < 1
          ) {
            return;
          }
          this._lastObservedWidth = width;
          requestAnimationFrame(() => this.measureContainer());
        });
        this._resizeObserver.observe(this.$refs.container);
      } else if (!this.windowScroll) {
        this._onResize = () =>
          requestAnimationFrame(() => this.measureContainer());
        window.addEventListener("resize", this._onResize);
      }
    });
  },
  beforeDestroy() {
    // Cancel any pending RAF to avoid mutating a destroyed instance
    if (this._rafId) cancelAnimationFrame(this._rafId);

    if (this.windowScroll) {
      if (this._windowScrollHandler) {
        window.removeEventListener("scroll", this._windowScrollHandler);
      }
      if (this._windowResizeHandler) {
        window.removeEventListener("resize", this._windowResizeHandler);
      }
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    } else if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
    }
  },
  methods: {
    onScroll() {
      if (!this.rafPending) {
        this.rafPending = true;
        this._rafId = requestAnimationFrame(() => {
          // Container-mode VERTICAL scroll drives scrollTop. In window mode the
          // container only scrolls HORIZONTALLY (overflow-y is hidden), so we
          // must NOT touch scrollTop here — window.scrollY drives the vertical
          // window via the window listener; overwriting it with the container's
          // scrollTop (always 0 in window mode) would snap the rows to the top.
          if (!this.windowScroll && this.$refs.container) {
            this.scrollTop = this.$refs.container.scrollTop;
          }
          this.updateScrollShadows();
          this.rafPending = false;
          this._rafId = null;
        });
      }
    },
    /** Toggle the left/right edge shadows from the container's horizontal scroll. */
    updateScrollShadows() {
      const el = this.$refs.container;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      this.canScrollLeft = el.scrollLeft > 1;
      this.canScrollRight = max > 1 && el.scrollLeft < max - 1;
    },
    measureContainer() {
      if (this.windowScroll) {
        this.containerHeight = window.innerHeight || 500;
      } else if (this.$refs.container) {
        this.containerHeight = this.$refs.container.clientHeight || 500;
      }
      // Re-measure row heights after container resize (text may wrap at
      // narrower widths, making rows taller than the initial measurement)
      this.$nextTick(() => this.measureRowHeight());
      this.updateScrollShadows();
    },
    measureRowHeight() {
      if (!this.$refs.container) return;
      // Measure all rendered data rows (exclude spacer rows)
      const rows = Array.from(
        this.$refs.container.querySelectorAll("tbody tr")
      ).filter((r) => !r.querySelector(".vt-spacer"));
      if (rows.length === 0) return;
      /**
       * Median, not max: occasional taller rows (pinned totals, group
       * subtotal rows, a wrapped cell) enter and leave the render window as
       * the user scrolls. With max, each entry/exit swings the estimated row
       * height, which repositions the spacer padding by (rows × delta) px —
       * visible as rapid flicker on large datasets. The median tracks the
       * typical row and stays stable regardless of which rows are rendered.
       */
      const heights = rows
        .map((r) => r.getBoundingClientRect().height)
        .filter((h) => h > 0)
        .sort((a, b) => a - b);
      if (heights.length === 0) return;
      const median = heights[Math.floor(heights.length / 2)];
      const newHeight = Math.max(this.rowHeight, Math.ceil(median));
      if (newHeight !== this.effectiveRowHeight) {
        this.effectiveRowHeight = newHeight;
      }
    },
    /**
     * Re-measure after the layout has settled. The first measurement on mount /
     * items change can run before the final width is known (a sidebar opening,
     * a horizontal scrollbar appearing, or fonts loading all shift cell
     * wrapping and therefore row height). Two RAFs let the browser finish
     * layout before we read heights, so the estimate doesn't start wrong and
     * flicker as it converges.
     */
    remeasureSoon() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.measureContainer();
          this.measureRowHeight();
          this.updateScrollShadows();
        });
      });
    },
    scrollToTop() {
      if (!this.windowScroll && this.$refs.container) {
        this.$refs.container.scrollTop = 0;
        this.scrollTop = 0;
      }
    },
  },
};
</script>

<style scoped>
.vt-wrapper {
  position: relative;
}
/**
 * Horizontal scroll-shadow affordances. A soft gradient on an edge tells the
 * user there are more columns to scroll to in that direction; it's toggled off
 * (v-show) once that edge is reached. pointer-events:none so it never blocks
 * clicks; full height so it stays visible while the page scrolls vertically.
 */
.vt-scroll-shadow {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 28px;
  pointer-events: none;
  z-index: 2;
}
.vt-scroll-shadow--right {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0));
}
.vt-scroll-shadow--left {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0));
}
.virtual-table-container {
  overflow-x: auto;
  overflow-anchor: none;
}
/**
 * Window-scroll mode: the page scrolls, so the container must NOT show its own
 * vertical scrollbar. Without this, overflow-x:auto forces overflow-y to
 * compute to `auto` (CSS spec disallows a visible/auto pair), and a wide
 * table's horizontal scrollbar then eats client height and spawns a phantom
 * vertical scrollbar that flickers. Setting overflow-y explicitly breaks that.
 */
.virtual-table-container--window {
  overflow-y: hidden;
}
.virtual-table-container--scrollable {
  height: calc(100vh - 300px);
  min-height: 300px;
  overflow-y: auto;
}
.vt-spacer {
  padding: 0 !important;
  border: none !important;
  line-height: 0;
  font-size: 0;
}
</style>
